import React, { createContext, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/auth/AuthContext";

const LabAccessContext = createContext(null);

export const LabAccessProvider = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, loading: authLoading } = useAuth();

    const [labs, setLabs] = useState([]);
    const [loading, setLoading] = useState(true);

    const getLabAccess = (labId) =>
        Array.isArray(labs)
            ? labs.find(l => String(l.id) === String(labId)) || null
            : null;

    const loadLabAccess = async () => {
        try {
            setLoading(true);

            const response = await fetch("/api/labs/access", {
                method: "GET",
                credentials: "include",
                headers: { Accept: "application/json" }
            });

            if (!response.ok)
                throw new Error(`Unable to load labs: ${response.status}`);

            const data = await response.json();

            if (!data.success)
                throw new Error(data.message || "Unable to load labs");

            setLabs(Array.isArray(data.labs) ? data.labs : []);
        } catch (error) {
            console.error("Lab loading error:", error);
            setLabs([]);
        } finally {
            setLoading(false);
        }
    };

    const openLab = (labId) => {
        if (!labId) {
            console.error("openLab: labId is required");
            return;
        }

        if (loading) {
            console.warn("Lab access is still loading.");
            return;
        }

        const lab = getLabAccess(labId);

        if (!lab) {
            navigate("/rect/labs");
            return;
        }

        if (lab.access_type === "free") {
            navigate("/rect/contact");
            return;
        }

        if (!isAuthenticated) {
            navigate("/login", {
                state: {
                    from: location.pathname,
                    labId
                }
            });
            return;
        }

        if (!lab.subscribed) {
            navigate(`/rect/app/labs/pricing/${labId}`);
            return;
        }

        console.log(`LAB ID.......  ${labId}`);

        navigate(`/rect/labs/loader/${labId}`, {
            state: { from: location.pathname }
        });
    };

    const openProject = (projectId, labId, ideType, level) => {
        console.log(`OPEN PROJECT::: ${projectId}`);
        if (!projectId) {
            console.error("openProject: projectId is required");
            return;
        }
        console.error(`/rect/labs/loader/${projectId}/${labId}/${ideType}/${level}`);
    
        navigate(`/rect/labs/loader/${projectId}/${labId}/${ideType}/${level}`, {
            state: { from: location.pathname }
        });
    };

    const hasLabAccess = (labId) =>
        Boolean(getLabAccess(labId)?.subscribed);

    const getCurrentPlan = (labId) => {
        const lab = getLabAccess(labId);
        if (!lab) return null;

        if (Array.isArray(lab.plans)) {
            const current = lab.plans.find(p => p.current === true);
            if (current) return current;
        }

        const planName = lab.subscription?.plan;

        if (planName && Array.isArray(lab.plans)) {
            return lab.plans.find(
                p =>
                    p.id === planName ||
                    p.id?.endsWith(`_${planName}`)
            ) || null;
        }

        return null;
    };

    const getLabPlans = (labId) => {
        const lab = getLabAccess(labId);
        return Array.isArray(lab?.plans) ? lab.plans : [];
    };

    const refreshLabAccess = loadLabAccess;

    useEffect(() => {
        if (!authLoading) loadLabAccess();
    }, [authLoading]);

    return (
        <LabAccessContext.Provider
            value={{
                labs,
                loading,
                isAuthenticated,
                openLab,
                openProject,
                getLabAccess,
                hasLabAccess,
                getCurrentPlan,
                getLabPlans,
                refreshLabAccess
            }}
        >
            {children}
        </LabAccessContext.Provider>
    );
};

export const useLabAccess = () => {
    const context = useContext(LabAccessContext);

    if (!context)
        throw new Error(
            "useLabAccess must be used inside LabAccessProvider"
        );

    return context;
};